import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DollarSignIcon, TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { formatCurrency } from '../../utils/currencyFormatter';
export function BudgetVsActualReport({
  filters,
  locale
}) {
  // Sample data for budget vs actual report
  const budgetData = [{
    department: 'Marketing',
    budget: 150000,
    actual: 142500,
    variance: -7500
  }, {
    department: 'Sales',
    budget: 200000,
    actual: 215000,
    variance: 15000
  }, {
    department: 'IT',
    budget: 300000,
    actual: 285000,
    variance: -15000
  }, {
    department: 'Operations',
    budget: 250000,
    actual: 248000,
    variance: -2000
  }, {
    department: 'HR',
    budget: 100000,
    actual: 98500,
    variance: -1500
  }, {
    department: 'Finance',
    budget: 120000,
    actual: 115000,
    variance: -5000
  }];
  const monthlyData = [{
    month: 'Jan',
    budget: 95000,
    actual: 92000
  }, {
    month: 'Feb',
    budget: 100000,
    actual: 98500
  }, {
    month: 'Mar',
    budget: 110000,
    actual: 115000
  }, {
    month: 'Apr',
    budget: 105000,
    actual: 107000
  }, {
    month: 'May',
    budget: 115000,
    actual: 110000
  }, {
    month: 'Jun',
    budget: 120000,
    actual: 118000
  }];
  const totalBudget = budgetData.reduce((sum, item) => sum + item.budget, 0);
  const totalActual = budgetData.reduce((sum, item) => sum + item.actual, 0);
  const totalVariance = totalActual - totalBudget;
  const variancePercentage = totalVariance / totalBudget * 100;
  return <div className="p-6" data-id="element-13388">
      <div className="mb-8" data-id="element-13389">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-13390">
          Budget vs Actual Spend Report
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-13391">
          Compare budgeted amounts against actual spend by department, GL code,
          or entity
        </p>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-13392">
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-13393">
          <div className="p-5" data-id="element-13394">
            <div className="flex items-center" data-id="element-13395">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3" data-id="element-13396">
                <DollarSignIcon className="h-6 w-6 text-white" data-id="element-13397" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-13398">
                <dl data-id="element-13399">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-13400">
                    Total Budget
                  </dt>
                  <dd data-id="element-13401">
                    <div className="text-lg font-medium text-gray-900" data-id="element-13402">
                      {formatCurrency(totalBudget, 'USD', locale)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-13403">
          <div className="p-5" data-id="element-13404">
            <div className="flex items-center" data-id="element-13405">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3" data-id="element-13406">
                <DollarSignIcon className="h-6 w-6 text-white" data-id="element-13407" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-13408">
                <dl data-id="element-13409">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-13410">
                    Total Actual
                  </dt>
                  <dd data-id="element-13411">
                    <div className="text-lg font-medium text-gray-900" data-id="element-13412">
                      {formatCurrency(totalActual, 'USD', locale)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-13413">
          <div className="p-5" data-id="element-13414">
            <div className="flex items-center" data-id="element-13415">
              <div className={`flex-shrink-0 ${totalVariance < 0 ? 'bg-green-500' : 'bg-red-500'} rounded-md p-3`} data-id="element-13416">
                {totalVariance < 0 ? <TrendingDownIcon className="h-6 w-6 text-white" data-id="element-13417" /> : <TrendingUpIcon className="h-6 w-6 text-white" data-id="element-13418" />}
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-13419">
                <dl data-id="element-13420">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-13421">
                    Variance
                  </dt>
                  <dd data-id="element-13422">
                    <div className="text-lg font-medium text-gray-900" data-id="element-13423">
                      {formatCurrency(totalVariance, 'USD', locale)} (
                      {variancePercentage.toFixed(1)}%)
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Department Chart */}
      <div className="bg-white p-6 rounded-lg shadow mb-8" data-id="element-13424">
        <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-13425">
          Budget vs Actual by Department
        </h4>
        <div className="h-80" data-id="element-13426">
          <ResponsiveContainer width="100%" height="100%" data-id="element-13427">
            <BarChart data={budgetData} margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-13428">
              <CartesianGrid strokeDasharray="3 3" data-id="element-13429" />
              <XAxis dataKey="department" data-id="element-13430" />
              <YAxis data-id="element-13431" />
              <Tooltip formatter={value => formatCurrency(value, 'USD', locale)} data-id="element-13432" />
              <Legend data-id="element-13433" />
              <Bar dataKey="budget" name="Budget" fill="#3b82f6" data-id="element-13434" />
              <Bar dataKey="actual" name="Actual" fill="#10b981" data-id="element-13435" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Monthly Trend */}
      <div className="bg-white p-6 rounded-lg shadow mb-8" data-id="element-13436">
        <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-13437">
          Monthly Budget vs Actual Trend
        </h4>
        <div className="h-80" data-id="element-13438">
          <ResponsiveContainer width="100%" height="100%" data-id="element-13439">
            <BarChart data={monthlyData} margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-13440">
              <CartesianGrid strokeDasharray="3 3" data-id="element-13441" />
              <XAxis dataKey="month" data-id="element-13442" />
              <YAxis data-id="element-13443" />
              <Tooltip formatter={value => formatCurrency(value, 'USD', locale)} data-id="element-13444" />
              <Legend data-id="element-13445" />
              <Bar dataKey="budget" name="Budget" fill="#3b82f6" data-id="element-13446" />
              <Bar dataKey="actual" name="Actual" fill="#10b981" data-id="element-13447" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Department Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-13448">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-13449">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-13450">
            Department Budget Details
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-13451">
            Detailed breakdown by department
          </p>
        </div>
        <div className="overflow-x-auto" data-id="element-13452">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-13453">
            <thead className="bg-gray-50" data-id="element-13454">
              <tr data-id="element-13455">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13456">
                  Department
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13457">
                  Budget
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13458">
                  Actual
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13459">
                  Variance
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13460">
                  % Variance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-13461">
              {budgetData.map((dept, index) => <tr key={index} data-id="element-13462">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-13463">
                    {dept.department}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-13464">
                    {formatCurrency(dept.budget, 'USD', locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-13465">
                    {formatCurrency(dept.actual, 'USD', locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right" data-id="element-13466">
                    <span className={`${dept.variance < 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-13467">
                      {formatCurrency(dept.variance, 'USD', locale)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right" data-id="element-13468">
                    <span className={`${dept.variance < 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-13469">
                      {(dept.variance / dept.budget * 100).toFixed(1)}%
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}